import { WrapperEntitySelector, Duration } from '@components/Inputs';
import { Typography, Checkbox, Grid } from '@ts/storybook';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import { CEntity, CKPIOperation, IEntity, TNestedKeys } from '@ts/ui-models';

import { KPIOperationType, KPIOperationDataDto } from '@ts/ui-models';
import { Fragment } from 'react/jsx-runtime';
import KPIOperationEventFilterInput from './KpiOperationEventFilterInput';

interface IKpiOperationInputsProps {
  values: CKPIOperation;
  type: KPIOperationType;
  onChangeData: (attrPath: string, value: any) => void;
  disabled: boolean;
}

const KpiOperationInputs = (props: IKpiOperationInputsProps) => {
  const data = props.values.data || ({} as KPIOperationDataDto);

  //#region Handlers
  const onChangeData = (attr: TNestedKeys<KPIOperationDataDto>) => (value: any) => {
    if (!attr) return;
    props.onChangeData(`data.${attr}`, value);
  };
  //#endregion

  //#region Render
  const renderMinMaxThresholds = () => {
    return (
      <Fragment key="thresholds">
        <Grid size={12}>
          <Typography key="title" variant="h6" text="Threshold" mt={4} />
        </Grid>
        <Grid container spacing={2}>
          <Grid size={12}>
            <Checkbox
              checked={data.maxThreshold?.enabled}
              onChangeValue={onChangeData('maxThreshold.enabled' as TNestedKeys<KPIOperationDataDto>)}
              label="Enable maximum threshold"
              isReadOnly={props.disabled}
            />
          </Grid>
          <Grid size={12}>
            <Duration
              key="maxThreshold"
              value={data.maxThreshold?.value}
              onChange={onChangeData('maxThreshold.value' as TNestedKeys<KPIOperationDataDto>)}
              disabled={!data.maxThreshold?.enabled || props.disabled}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid size={12}>
            <Checkbox
              checked={data.minThreshold?.enabled}
              onChangeValue={onChangeData('minThreshold.enabled' as TNestedKeys<KPIOperationDataDto>)}
              label="Enable minimum threshold"
              isReadOnly={props.disabled}
            />
          </Grid>
          <Grid size={12}>
            <Duration
              key="minThreshold"
              value={data.minThreshold?.value}
              onChange={onChangeData('minThreshold.value' as TNestedKeys<KPIOperationDataDto>)}
              disabled={!data.minThreshold?.enabled || props.disabled}
            />
          </Grid>
        </Grid>
      </Fragment>
    );
  };

  const renderEventInputs = () => {
    switch (props.type) {
      case 'inter_event_item_usage':
      case 'time_between_events':
      case 'interval_summation':
        return (
          <>
            <KPIOperationEventFilterInput
              {...props}
              key="startEventType"
              //@ts-ignore because dtos are not typed
              value={data.startEventType || {}}
              label="First event type"
              onChangeData={onChangeData('startEventType')}
            />
            <KPIOperationEventFilterInput
              {...props}
              key="endEventType"
              //@ts-ignore because dtos are not typed
              value={data.endEventType || {}}
              label="Second event type"
              onChangeData={onChangeData('endEventType')}
            />
            {renderMinMaxThresholds()}
          </>
        );
      case 'count_events':
        return (
          <KPIOperationEventFilterInput
            {...props}
            key={'eventType'}
            //@ts-ignore because dtos are not typed
            value={data.eventType || {}}
            label={'Event type'}
            onChangeData={onChangeData('eventType')}
          />
        );
      case 'value_between_readings':
        return (
          <Grid size={6}>
            <WrapperEntitySelector
              key={'readingType'}
              label={'Reading type'}
              searchComponent={generateSearchEntitySelector('readingType')}
              value={data.readingTypeId}
              onChangeEntity={entity => {
                onChangeData('readingTypeId' as TNestedKeys<KPIOperationDataDto>)((entity as CEntity<IEntity>)?.getID());
              }}
              isReadOnly={props.disabled}
            />
          </Grid>
        );
      default:
        return null;
    }
  };
  //#endregion

  return (
    <Grid container size={12}>
      <Grid size={12}>
        <Typography key="title" variant="h6" gutterBottom text="Input data" />
      </Grid>
      <Grid container spacing={2} size={12}>
        {renderEventInputs()}
      </Grid>
    </Grid>
  );
};

export default KpiOperationInputs;
